import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  NAVIGATION, NAVIGATION_WITH_DASHBOARD, DASHBOARD_ITEM, DEFAULT_PINNED, MAX_PINNED,
  PINNED_STORAGE_KEY, EXPANDED_STORAGE_KEY,
  flattenNav, findCategoryPath, isRouteActive,
} from './navigationConfig'
import { Search, X, ChevronRight, Pin, PinOff } from 'lucide-react'

// ─── localStorage helpers ────────────────────────────────────────────────────
function loadPinned() {
  try {
    const raw = localStorage.getItem(PINNED_STORAGE_KEY)
    if (!raw) return DEFAULT_PINNED.slice(0, MAX_PINNED)
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return DEFAULT_PINNED.slice(0, MAX_PINNED)
    return parsed.slice(0, MAX_PINNED)
  } catch {
    return DEFAULT_PINNED.slice(0, MAX_PINNED)
  }
}

function loadExpanded() {
  try {
    const raw = localStorage.getItem(EXPANDED_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : null
  } catch {
    return null
  }
}

function savePinned(list) {
  try { localStorage.setItem(PINNED_STORAGE_KEY, JSON.stringify(list)) } catch {}
}

function saveExpanded(list) {
  try { localStorage.setItem(EXPANDED_STORAGE_KEY, JSON.stringify(list)) } catch {}
}

// ─── Leaf lookup map ─────────────────────────────────────────────────────────
const ALL_LEAVES = flattenNav(NAVIGATION_WITH_DASHBOARD)
const LEAF_BY_ROUTE = new Map(ALL_LEAVES.map((l) => [l.to, l]))

// Static indentation classes (Tailwind can't analyze dynamic class names).
const INDENT = ['', 'pl-8', 'pl-12', 'pl-16']

// ─── Sidebar component ────────────────────────────────────────────────────────
export default function AdminSidebar({ sidebarOpen, onClose }) {
  const location = useLocation()
  const currentPath = location.pathname

  const [pinned, setPinned] = useState(loadPinned)
  const [search, setSearch] = useState('')
  const [searchFocused, setSearchFocused] = useState(false)
  const searchRef = useRef(null)
  const containerRef = useRef(null)

  // Compute auto-expanded categories from the active route.
  const activeCategoryPath = useMemo(
    () => findCategoryPath(currentPath),
    [currentPath]
  )
  const autoExpandedIds = useMemo(
    () => activeCategoryPath.map((n) => n.id).filter(Boolean),
    [activeCategoryPath]
  )

  // Persisted expanded state. On first load, default to auto-expanded only.
  const [userExpanded, setUserExpanded] = useState(() => {
    const loaded = loadExpanded()
    return loaded === null ? [] : loaded
  })

  // Track categories the user has explicitly collapsed (overrides auto-expand).
  const [userCollapsed, setUserCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem('sma.admin.collapsed')
      if (!raw) return new Set()
      const parsed = JSON.parse(raw)
      return new Set(Array.isArray(parsed) ? parsed : [])
    } catch {
      return new Set()
    }
  })

  // Effective expanded set = auto-expanded categories minus user-collapsed,
  // plus user-expanded categories that aren't auto-expanded.
  const expandedSet = useMemo(() => {
    const set = new Set()
    // Auto-expand active route's categories (unless user explicitly collapsed them).
    autoExpandedIds.forEach((id) => {
      if (!userCollapsed.has(id)) set.add(id)
    })
    // Add user-expanded categories (unless they're auto-expanded and user collapsed them).
    userExpanded.forEach((id) => {
      if (!userCollapsed.has(id)) set.add(id)
    })
    return set
  }, [userExpanded, autoExpandedIds, userCollapsed])

  const toggleExpanded = useCallback((id) => {
    const isCurrentlyExpanded = expandedSet.has(id)
    if (isCurrentlyExpanded) {
      // Collapsing: if it's auto-expanded, add to userCollapsed; otherwise remove from userExpanded.
      if (autoExpandedIds.includes(id)) {
        setUserCollapsed((prev) => {
          const next = new Set(prev)
          next.add(id)
          try { localStorage.setItem('sma.admin.collapsed', JSON.stringify(Array.from(next))) } catch {}
          return next
        })
      } else {
        setUserExpanded((prev) => {
          const next = new Set(prev)
          next.delete(id)
          const arr = Array.from(next)
          saveExpanded(arr)
          return next
        })
      }
    } else {
      // Expanding: remove from userCollapsed, add to userExpanded.
      setUserCollapsed((prev) => {
        const next = new Set(prev)
        next.delete(id)
        try { localStorage.setItem('sma.admin.collapsed', JSON.stringify(Array.from(next))) } catch {}
        return next
      })
      setUserExpanded((prev) => {
        const next = new Set(prev)
        next.add(id)
        const arr = Array.from(next)
        saveExpanded(arr)
        return next
      })
    }
  }, [expandedSet, autoExpandedIds])

  const togglePin = useCallback((route) => {
    setPinned((prev) => {
      let next
      if (prev.includes(route)) {
        next = prev.filter((r) => r !== route)
      } else if (prev.length < MAX_PINNED) {
        next = [...prev, route]
      } else {
        // Replace the last pinned item.
        next = [...prev.slice(0, MAX_PINNED - 1), route]
      }
      savePinned(next)
      return next
    })
  }, [])

  // ─── Keyboard shortcut: '/' focuses search ────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== '/' || searchFocused) return
      const t = e.target
      const tag = t?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || t?.isContentEditable) return
      e.preventDefault()
      searchRef.current?.focus()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [searchFocused])

  // ─── Search filtering ──────────────────────────────────────────────────────
  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return null
    return flattenNav(NAVIGATION_WITH_DASHBOARD).filter((item) => {
      const label = item.label.toLowerCase()
      const route = item.to.toLowerCase()
      return label.includes(q) || route.includes(q)
    })
  }, [search])

  const isSearching = !!search.trim()

  // ─── Render helpers ────────────────────────────────────────────────────────
  const renderItem = (item, depth = 0) => {
    const active = isRouteActive(currentPath, item.to)
    const isPinned = pinned.includes(item.to)
    return (
      <div
        key={item.to}
        className={`group flex items-center transition-colors ${INDENT[depth] || ''}`}
      >
        <Link
          to={item.to}
          onClick={onClose}
          aria-current={active ? 'page' : undefined}
          className={`flex-1 flex items-center gap-2.5 py-2 pr-2 text-sm transition-colors font-sans ${
            active
              ? 'text-primary-600 dark:text-primary-400 font-medium'
              : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
          }`}
        >
          {item.icon && (
            <item.icon
              size={16}
              className={active ? 'text-primary-500' : 'text-gray-400 dark:text-gray-500'}
            />
          )}
          <span className="truncate">{item.label}</span>
        </Link>
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin(item.to) }}
          aria-label={isPinned ? `Unpin ${item.label}` : `Pin ${item.label}`}
          title={isPinned ? 'Unpin' : 'Pin'}
          className="opacity-0 group-hover:opacity-100 focus:opacity-100 p-1 mr-1 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-opacity"
        >
          {isPinned ? <PinOff size={12} /> : <Pin size={12} />}
        </button>
      </div>
    )
  }

  const renderGroup = (node, depth = 0) => {
    // Leaf node (has `to`) — skip if pinned (shown in Pinned section instead)
    if (node.to) {
      if (pinned.includes(node.to)) return null
      return renderItem(node, depth)
    }

    // Subgroup heading — visual label only, no chevron, no toggle.
    // Children are always visible when the parent category is expanded.
    if (node.subgroup) {
      const visibleChildren = node.children.filter((child) => {
        if (child.to && pinned.includes(child.to)) return false
        return true
      })
      if (visibleChildren.length === 0) return null
      return (
        <div key={node.id} className="select-none">
          <div
            className={`py-1.5 mt-1 ${INDENT[depth] || ''} text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500`}
          >
            {node.label}
          </div>
          <div className="mb-1">
            {visibleChildren.map((child) => renderGroup(child, depth + 1))}
          </div>
        </div>
      )
    }

    // Group node (collapsible category)
    const id = node.id
    const expanded = expandedSet.has(id)
    const hasActiveChild = autoExpandedIds.includes(id)
    // Filter out pinned children at the leaf level; keep sub-groups intact.
    const visibleChildren = node.children.filter((child) => {
      if (child.to && pinned.includes(child.to)) return false
      return true
    })
    return (
      <div key={id} className="select-none">
        <button
          type="button"
          onClick={() => toggleExpanded(id)}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${node.label}`}
          className={`w-full flex items-center justify-between py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
            depth === 0
              ? 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              : 'text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
          } ${INDENT[depth] || ''}`}
        >
          <span>{node.label}</span>
          <ChevronRight
            size={14}
            className={`text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`}
          />
        </button>
        {expanded && visibleChildren.length > 0 && (
          <div className={depth === 0 ? 'mt-0.5 mb-2' : 'mt-0.5 mb-1'}>
            {visibleChildren.map((child) => renderGroup(child, depth + 1))}
          </div>
        )}
      </div>
    )
  }

  // ─── Pinned section ────────────────────────────────────────────────────────
  const pinnedItems = useMemo(() => {
    return pinned
      .map((route) => LEAF_BY_ROUTE.get(route))
      .filter(Boolean)
  }, [pinned])

  const renderPinnedItem = (item) => {
    const active = isRouteActive(currentPath, item.to)
    return (
      <div key={item.to} className="group flex items-center">
        <Link
          to={item.to}
          onClick={onClose}
          aria-current={active ? 'page' : undefined}
          className={`flex-1 flex items-center gap-2.5 py-2 pr-2 text-sm transition-colors font-sans ${
            active
              ? 'text-primary-600 dark:text-primary-400 font-medium'
              : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
          }`}
        >
          {item.icon && (
            <item.icon
              size={16}
              className={active ? 'text-primary-500' : 'text-gray-400 dark:text-gray-500'}
            />
          )}
          <span className="truncate">{item.label}</span>
        </Link>
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin(item.to) }}
          aria-label={`Unpin ${item.label}`}
          title="Unpin"
          className="opacity-0 group-hover:opacity-100 focus:opacity-100 p-1 mr-1 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-opacity"
        >
          <PinOff size={12} />
        </button>
      </div>
    )
  }

  // ─── Search results rendering ──────────────────────────────────────────────
  const renderSearchResult = (item) => {
    const active = isRouteActive(currentPath, item.to)
    const breadcrumb = item.parents.map((p) => p.label).join(' › ')
    return (
      <Link
        key={item.to}
        to={item.to}
        onClick={onClose}
        aria-current={active ? 'page' : undefined}
        className={`block py-2 px-2 rounded-none text-sm transition-colors ${
          active
            ? 'text-primary-600 dark:text-primary-400 font-medium'
            : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-800/50'
        }`}
      >
        <div className="flex items-center gap-2">
          {item.icon && <item.icon size={14} className="text-gray-400 flex-shrink-0" />}
          <span className="truncate">{item.label}</span>
        </div>
        {breadcrumb && (
          <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 pl-6 truncate">
            {breadcrumb}
          </div>
        )}
      </Link>
    )
  }

  return (
    <aside
      ref={containerRef}
      className={`
        fixed top-16 left-0 bottom-0 w-64 z-40 overflow-y-auto transition-transform duration-300 ease-in-out
        bg-white dark:bg-cinema-charcoal border-r border-gray-200 dark:border-white/10 shadow-2xl shadow-black/50
        lg:static lg:top-auto lg:bottom-auto lg:h-full lg:shadow-none lg:translate-x-0 lg:flex-shrink-0
        p-3 lg:p-4
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}
    >
      {/* Search */}
      <div className="mb-4">
        <div className="relative">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <input
            ref={searchRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            placeholder="Search navigation…  /"
            aria-label="Search navigation"
            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 rounded-none focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
          />
          {search && (
            <button
              type="button"
              onClick={() => { setSearch(''); searchRef.current?.focus() }}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {isSearching ? (
        /* ─── Search results ─── */
        <div className="space-y-0.5">
          {searchResults && searchResults.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 px-2 py-4">
              No matches for "{search}"
            </p>
          ) : (
            searchResults?.map(renderSearchResult)
          )}
        </div>
      ) : (
        <>
          {/* ─── Pinned ─── */}
          {pinnedItems.length > 0 && (
            <div className="mb-4">
              <div className="px-2 py-1 mb-1">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                  Pinned
                </span>
              </div>
              <div className="space-y-0.5">
                {pinnedItems.map(renderPinnedItem)}
              </div>
            </div>
          )}

          {/* ─── Categories ─── */}
          <nav aria-label="Admin navigation">
            {NAVIGATION.map((cat) => renderGroup(cat, 0))}
          </nav>
        </>
      )}
    </aside>
  )
}
